import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import { useNotification } from './NotificationContext';
import { API_URL } from '../config';

const NotificationCenterContext = createContext();

// Matches the backend default. GET /api/notifications clamps limit at 100.
const PAGE_SIZE = 30;

// Merge a server page into what we already hold, keyed by _id, newest first.
// Both sides can carry rows the other lacks: the socket delivers a notification
// the instant it is created, while a page response may have been queried a tick
// earlier — so neither side may clobber the other, only merge.
const mergeById = (existing, incoming) => {
  const byId = new Map(existing.map(n => [n._id, n]));
  for (const row of incoming) {
    const held = byId.get(row._id);
    // isRead is monotonic here — nothing in the app ever marks a row unread —
    // so an optimistic local read must survive a page that was queried before
    // that read committed.
    byId.set(row._id, held ? { ...held, ...row, isRead: held.isRead || row.isRead } : row);
  }
  return Array.from(byId.values())
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

export const useNotificationCenter = () => {
  const context = useContext(NotificationCenterContext);
  if (!context) {
    throw new Error('useNotificationCenter must be used within a NotificationCenterProvider');
  }
  return context;
};

export const NotificationCenterProvider = ({ children }) => {
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  const { showNotification, permission } = useNotification();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // notifications/unreadCount are mirrored into refs that are written
  // SYNCHRONOUSLY on every change (see applyNotifications/applyUnreadCount).
  // Everything that has to reason about the current list — the socket dedupe,
  // the loadMore cursor, the optimistic rollbacks — reads the ref, so two
  // events landing in the same tick each see the real list instead of a stale
  // render snapshot and double-counting becomes impossible.
  const notificationsRef = useRef(notifications);
  const unreadCountRef = useRef(unreadCount);

  // Values the socket handler needs but must not re-register for. Mirroring
  // them keeps the listener effect on [socket, isConnected] deps only — the
  // same reason ChatContext does this ("Minimal dependencies to avoid
  // re-registering"): a re-register drops events during the gap.
  const showNotificationRef = useRef(showNotification);
  const permissionRef = useRef(permission);

  const mountedRef = useRef(true);
  const pageControllerRef = useRef(null); // first page / refresh lane
  const moreControllerRef = useRef(null); // loadMore lane
  const loadingMoreRef = useRef(false);

  // Set while the first-page request is open, plus the ids the socket delivered
  // during it. That request's unreadCount was computed before those rows
  // existed, so the fetch adds them back instead of overwriting the count.
  const firstPageInFlightRef = useRef(false);
  const racedIdsRef = useRef(new Set());

  // Bumped by every read write. A first-page response carries an unreadCount the
  // server computed BEFORE any read that committed while the request was open,
  // so applying it would resurrect the badge the read just cleared — opening the
  // panel calls refresh(), and "mark all read" is the very next gesture, so that
  // window is hit routinely rather than rarely. Same guard-on-identity idea as
  // fetchFirstPage's finally block, one lane over.
  const readGenerationRef = useRef(0);

  useEffect(() => { showNotificationRef.current = showNotification; }, [showNotification]);
  useEffect(() => { permissionRef.current = permission; }, [permission]);

  // Setup (not just cleanup) resets mountedRef, otherwise StrictMode's
  // mount → unmount → remount leaves it false for the rest of the dev session.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pageControllerRef.current) pageControllerRef.current.abort();
      if (moreControllerRef.current) moreControllerRef.current.abort();
    };
  }, []);

  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }), []);

  // The only two writers of the list and the count. They update the ref first
  // so the ref, not React's queue, is the source of truth for the next value.
  const applyNotifications = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(notificationsRef.current) : updater;
    notificationsRef.current = next;
    setNotifications(next);
  }, []);

  const applyUnreadCount = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(unreadCountRef.current) : updater;
    // Clamp — a rollback racing a server count must never produce a negative badge.
    unreadCountRef.current = Math.max(0, next || 0);
    setUnreadCount(unreadCountRef.current);
  }, []);

  // ============================================
  // FETCHING
  // ============================================
  const fetchFirstPage = useCallback(async () => {
    if (!user?._id) return;

    // A newer first-page request supersedes the one in flight.
    if (pageControllerRef.current) pageControllerRef.current.abort();
    const controller = new AbortController();
    pageControllerRef.current = controller;
    firstPageInFlightRef.current = true;
    racedIdsRef.current.clear();
    const readGeneration = readGenerationRef.current;

    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_URL}/api/notifications?limit=${PAGE_SIZE}`,
        { ...getAuthHeaders(), signal: controller.signal }
      );
      if (!mountedRef.current) return;

      const page = data.notifications || [];
      applyNotifications(prev => mergeById(prev, page));
      setHasMore(Boolean(data.hasMore));

      // A read committed while this was open, so its count predates the write.
      // markRead/markAllRead already applied the correct value — leave it alone.
      // The list merge above is still safe either way: mergeById keeps the local
      // isRead, so the rows stay read even though the count came from before.
      if (readGenerationRef.current !== readGeneration) return;

      // Add back every socket arrival this page cannot know about. Anything the
      // page does contain is already in data.unreadCount.
      const pageIds = new Set(page.map(n => n._id));
      let raced = 0;
      racedIdsRef.current.forEach(id => { if (!pageIds.has(id)) raced += 1; });
      applyUnreadCount((data.unreadCount || 0) + raced);
    } catch (error) {
      if (axios.isCancel(error)) return; // superseded by a newer fetch, or unmounted
      if (!mountedRef.current) return;
      // Silent degrade: this runs on every login, and an outage should not
      // greet the user with a toast they can do nothing about.
      console.error('❌ Error fetching notifications:', error);
    } finally {
      // Guard on identity — an aborted predecessor's finally must not clear the
      // in-flight tracking that its successor just set up.
      if (pageControllerRef.current === controller) {
        firstPageInFlightRef.current = false;
        racedIdsRef.current.clear();
        if (mountedRef.current) setLoading(false);
      }
    }
  }, [user?._id, getAuthHeaders, applyNotifications, applyUnreadCount]);

  const loadMore = useCallback(async () => {
    // loadingMoreRef is set synchronously below, so a double click or a
    // scroll handler firing twice in one tick cannot start two requests.
    if (!user?._id || loadingMoreRef.current || !hasMore) return;

    // Cursor = the oldest row we hold. Taken as a minimum rather than as the
    // last array element so an out-of-order arrival can never make us
    // re-request a page we already have.
    let before = null;
    for (const n of notificationsRef.current) {
      if (!before || new Date(n.createdAt) < new Date(before)) before = n.createdAt;
    }
    if (!before) return;

    loadingMoreRef.current = true;
    const controller = new AbortController();
    moreControllerRef.current = controller;

    try {
      setLoading(true);
      const beforeParam = encodeURIComponent(new Date(before).toISOString());
      const { data } = await axios.get(
        `${API_URL}/api/notifications?limit=${PAGE_SIZE}&before=${beforeParam}`,
        { ...getAuthHeaders(), signal: controller.signal }
      );
      if (!mountedRef.current) return;

      applyNotifications(prev => mergeById(prev, data.notifications || []));
      setHasMore(Boolean(data.hasMore));
      // data.unreadCount is deliberately ignored here — a socket arrival could
      // have raced this response, and the first page plus markRead already keep
      // the count authoritative.
    } catch (error) {
      if (axios.isCancel(error)) return;
      if (!mountedRef.current) return;
      console.error('❌ Error loading more notifications:', error);
      toast.error('Failed to load more notifications');
    } finally {
      if (moreControllerRef.current === controller) {
        loadingMoreRef.current = false;
        if (mountedRef.current) setLoading(false);
      }
    }
  }, [user?._id, hasMore, getAuthHeaders, applyNotifications]);

  const refresh = useCallback(() => {
    fetchFirstPage();
  }, [fetchFirstPage]);

  // ============================================
  // READ STATE (optimistic, with rollback)
  // ============================================
  const markRead = useCallback(async (id) => {
    if (!id) return;
    const target = notificationsRef.current.find(n => n._id === id);
    if (!target || target.isRead) return; // idempotent — never decrement twice

    readGenerationRef.current += 1;
    applyNotifications(prev => prev.map(n => (n._id === id ? { ...n, isRead: true } : n)));
    applyUnreadCount(prev => prev - 1);
    // Already accounted for: drop it so a first page still in flight does not
    // add it back as an unread arrival.
    racedIdsRef.current.delete(id);

    try {
      const { data } = await axios.post(
        `${API_URL}/api/notifications/${id}/read`,
        {},
        getAuthHeaders()
      );
      if (!mountedRef.current) return;
      if (typeof data?.unreadCount === 'number') applyUnreadCount(data.unreadCount);
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('❌ Error marking notification read:', error);
      applyNotifications(prev => prev.map(n => (n._id === id ? { ...n, isRead: false } : n)));
      applyUnreadCount(prev => prev + 1);
      toast.error('Failed to mark notification as read');
    }
  }, [getAuthHeaders, applyNotifications, applyUnreadCount]);

  const markAllRead = useCallback(async () => {
    const flipped = new Set(notificationsRef.current.filter(n => !n.isRead).map(n => n._id));
    const previousCount = unreadCountRef.current;
    if (flipped.size === 0 && previousCount === 0) return;

    readGenerationRef.current += 1;
    applyNotifications(prev => prev.map(n => (n.isRead ? n : { ...n, isRead: true })));
    applyUnreadCount(0);
    racedIdsRef.current.clear();

    try {
      await axios.post(`${API_URL}/api/notifications/read-all`, {}, getAuthHeaders());
      // No count is adopted from the response on purpose: read-all returns
      // unreadCount:0 by definition, and re-applying it would wipe a socket
      // arrival that legitimately bumped the badge while this was in flight.
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('❌ Error marking all notifications read:', error);
      // Roll back only the rows we flipped and only the count we zeroed, so a
      // notification that arrived over the socket mid-request survives intact.
      applyNotifications(prev => prev.map(n => (flipped.has(n._id) ? { ...n, isRead: false } : n)));
      applyUnreadCount(prev => prev + previousCount);
      toast.error('Failed to mark notifications as read');
    }
  }, [getAuthHeaders, applyNotifications, applyUnreadCount]);

  // ============================================
  // LIFECYCLE
  // ============================================
  // Load on login; wipe on logout so the next account on this browser never
  // sees the previous user's inbox.
  useEffect(() => {
    if (pageControllerRef.current) pageControllerRef.current.abort();
    if (moreControllerRef.current) moreControllerRef.current.abort();
    loadingMoreRef.current = false;
    racedIdsRef.current.clear();
    applyNotifications([]);
    applyUnreadCount(0);
    setHasMore(false);

    if (!user?._id) {
      setLoading(false);
      return;
    }

    fetchFirstPage();
  }, [user?._id, fetchFirstPage, applyNotifications, applyUnreadCount]);

  // Live delivery. Room membership (`user:${userId}`) is joined server-side on
  // socket authentication, so there is nothing to emit from here.
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNotificationNew = (data) => {
      const notification = data?.notification;
      if (!notification?._id) return;

      // The server emits the instant the row is created, which regularly beats
      // — or is beaten by — the first-page fetch, and both can carry the same
      // row. notificationsRef is exact within the tick, so this is the single
      // place duplicates are stopped.
      if (notificationsRef.current.some(n => n._id === notification._id)) return;

      applyNotifications(prev => [notification, ...prev]);
      if (!notification.isRead) applyUnreadCount(prev => prev + 1);
      if (firstPageInFlightRef.current) racedIdsRef.current.add(notification._id);

      if (document.hidden && permissionRef.current === 'granted') {
        // showNotification already no-ops on a focused tab, so the hidden check
        // above is what selects this branch rather than what enables it.
        showNotificationRef.current(notification.title, {
          body: notification.body || '',
          // Keyed on the ticket to match the web-push copy's tag: a hidden tab
          // that receives both the socket event and the push shows one OS
          // notification, not two.
          // Coaching rows key on their session, the web push's tag.
          tag: notification.coachingSession
            ? `clara-coaching-${notification.coachingSession}`
            : `clara-review-decision-${notification.ticket || notification._id}`,
          // The service worker dispatches on data.type; 'review_decision' is
          // its navigate-by-url branch, which uses data.url.
          data: {
            type: 'review_decision',
            url: notification.link,
            ticketId: notification.ticketId
          }
        });
        return;
      }

      // Focused tab (or no OS permission): the in-app toast is the notification.
      const description = notification.body || undefined;
      if (['review_approved', 'appeal_approved', 'coaching_agent_done'].includes(notification.type)) {
        toast.success(notification.title, { description, duration: 6000 });
      } else {
        toast.info(notification.title, { description, duration: 6000 });
      }
    };

    socket.on('notification:new', handleNotificationNew);

    return () => {
      // Pass the handler — a bare socket.off('notification:new') would also
      // strip listeners registered elsewhere.
      socket.off('notification:new', handleNotificationNew);
    };
  }, [socket, isConnected, applyNotifications, applyUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    loading,
    hasMore,
    loadMore,
    markRead,
    markAllRead,
    refresh
  };

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
    </NotificationCenterContext.Provider>
  );
};

export default NotificationCenterContext;
